import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../../../../../stores/root';

// assets
import VesselRequisitionItemStatus from '../../../../../../manifests/status/vesselRequisitionItem';

// services
import PermissionsService from '../../../../../../services/permissions';
import GeneralService from '../../../../../../services/general';
import VesselRequisitionItemService from '../../../../../../services/vessels/requisitions/item';

// props
import { IVesselRequisitionResourceShortProps } from '../../../../../../props/vessels/requisitions/requisition';
import { IVesselRequisitionItemResourceShortProps } from '../../../../../../props/vessels/requisitions/item';

// components
import { ActionButton, CommandBar, ContextualMenuItemType, Dropdown, IColumn, ICommandBarItemProps, IContextualMenuItem, ISelection, Selection, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from '../../../../../typography/text';
import Label from '../../../../../typography/label';
import Tag from '../../../../../uiframeworks/tag';
import ApproveVesselRequisitionItem from '../form/approve';
import RejectVesselRequisitionItem from '../form/reject';
import UpdateVesselRequisitionItemPricing from '../form/pricings/update';
import VesselRequisitionItemApprovalStatus from '../../../../../../manifests/status/vesselRequisitionItemApproval';
import VesselRequisitionItemPricingStatus from '../../../../../../manifests/status/vesselRequisitionItemPricing';
import VesselRequisitionItemDeliveryStatus from '../../../../../../manifests/status/vesselRequisitionItemDelivery';
import ApproveVesselRequisitionItemPricing from '../form/pricings/approve';
import RejectVesselRequisitionItemPricing from '../form/pricings/reject';

interface VesselRequisitionItemsListProps {
    requisitionId: string;
}

const VesselRequisitionItemsList: React.FC<VesselRequisitionItemsListProps> = observer((props: VesselRequisitionItemsListProps) => {
    const { user } = useStore();

    const [allItems, setAllItems] = React.useState<IVesselRequisitionItemResourceShortProps[]>([]);
    const [filteredItems, setFilteredItems] = React.useState<IVesselRequisitionItemResourceShortProps[]>([]);
    const [selected, setSelected] = React.useState<IVesselRequisitionItemResourceShortProps[]>([]);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [selectedStatus, setSelectedStatus] = React.useState<string>("all");

    const selection: ISelection = new Selection({
        onSelectionChanged: () => {
            setSelected(selection.getSelection() as IVesselRequisitionItemResourceShortProps[])
        },
    });

    const columns: IColumn[] = [
        {
            key: "name",
            name: 'Name',
            minWidth: 175,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const showWeight = item.goods?.weightAmount && Number(item.goods?.weightAmount) !== 0;
                const showSize = (item.goods?.length && Number(item.goods?.length) !== 0) || (item.goods?.width && Number(item.goods?.width) !== 0) || (item.goods?.height && Number(item.goods?.height) !== 0);

                return <Stack styles={{ root: { padding: '4px 0px' } }} tokens={{ childrenGap: 3 }}>
                    <Stack>
                        <Label size={'xsmall'}>{item.goods.name}</Label>
                        {item.notes ? <Text size={'small'} style={{ fontStyle: 'italic' }}>Notes: {item.notes}</Text> : null}
                    </Stack>
                    {showSize ? <Stack horizontal tokens={{ childrenGap: 5 }}>
                        <Text size={'small'} style={{ width: 40 }}>Size</Text>
                        <Text size={'small'}>:</Text>
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.length))} cm</Text>
                            <Text size={'small'}>x</Text>
                            <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.width))} cm</Text>
                            <Text size={'small'}>x</Text>
                            <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.height))} cm</Text>
                        </Stack>
                    </Stack> : null}
                    {showWeight ? <Stack horizontal tokens={{ childrenGap: 5 }}>
                        <Text size={'small'} style={{ width: 40 }}>Weight</Text>
                        <Text size={'small'}>:</Text>
                        <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.weightAmount))} {item.goods?.weightUnit}</Text>
                    </Stack> : null}
                </Stack>
            }
        },
        {
            key: "purpose",
            name: 'Purpose',
            minWidth: 150,
            maxWidth: 150,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{item.purpose}</Text>
                </Stack>
            }
        },
        {
            key: "vendor",
            name: 'Vendor',
            minWidth: 150,
            maxWidth: 150,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.pricing ? <Text size={'small'}>{item.pricing?.vendor.name}</Text> : null}
                    {!item.pricing ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        },
        {
            key: "totalPrice",
            name: 'Total Price',
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.pricing ? <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(item.pricing?.finalPrice)}</Text> : null}
                    {!item.pricing ? <Text size={'small'} style={{ fontStyle: 'italic' }}>-</Text> : null}
                </Stack>
            }
        },
        {
            key: "quantity",
            name: 'Quantity',
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.quantityAmount))} {item.quantityUnit.name}</Text>
                </Stack>
            }
        },
        {
            key: "approvalStatus",
            name: "Approval",
            minWidth: 75,
            maxWidth: 75,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const status = VesselRequisitionItemApprovalStatus.find((r) => r.key === item.approvalStatus);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    <Tag size={'small'} text={status?.text} variant={status?.tagVariant} />
                </Stack>
            }
        },
        {
            key: "pricingStatus",
            name: "Pricing",
            minWidth: 75,
            maxWidth: 75,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const status = VesselRequisitionItemPricingStatus.find((r) => r.key === item.pricingStatus);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    {item.approvalStatus === 'approved' ? <Tag size={'small'} text={status?.text} variant={status?.tagVariant} /> : null}
                    {item.approvalStatus !== 'approved' ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        },
        {
            key: "paymentStatus",
            name: "Payment",
            minWidth: 75,
            maxWidth: 75,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const status = VesselRequisitionItemPricingStatus.find((r) => r.key === item.paymentStatus);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    {item.pricingStatus === 'approved' ? <Tag size={'small'} text={status?.text} variant={status?.tagVariant} /> : null}
                    {item.pricingStatus !== 'approved' ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        },
        {
            key: "deliveryStatus",
            name: "Delivery",
            minWidth: 75,
            maxWidth: 75,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const status = VesselRequisitionItemDeliveryStatus.find((r) => r.key === item.deliveryStatus);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    {item.pricingStatus === 'approved' ? <Tag size={'small'} text={status?.text} variant={status?.tagVariant} /> : null}
                    {item.pricingStatus !== 'approved' ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        }
    ];

    useEffect(() => {
        _onRetrieveItems();
    }, []);

    const getCommandBarItems = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        const canApprove = (!(!loaded || selected.length < 1 || (selected.findIndex((s) => s.approvalStatus !== 'rejected' && s.approvalStatus !== 'pending') > -1))) && PermissionsService.hasPermission(['vessels.requisitions.approvals.approve_item'], user.permissions);
        const canReject = (!(!loaded || selected.length < 1 || (selected.findIndex((s) => s.approvalStatus === 'rejected') > -1))) && PermissionsService.hasPermission(['vessels.requisitions.approvals.reject_item'], user.permissions);

        const canApprovePricing = (!(!loaded || selected.length < 1 || (selected.findIndex((s) => (s.pricingStatus !== 'rejected' && s.pricingStatus !== 'pending') || !s.pricing) > -1))) && PermissionsService.hasPermission(['vessels.requisitions.approvals.approve_price'], user.permissions);
        const canRejectPricing = (!(!loaded || selected.length < 1 || (selected.findIndex((s) => s.pricingStatus === 'rejected' || !s.pricing) > -1))) && PermissionsService.hasPermission(['vessels.requisitions.approvals.reject_price'], user.permissions);

        const subitems: IContextualMenuItem[] = [];

        if (canApprove || canReject) {
            subitems.push({ key: 'header', text: 'Approval', itemType: ContextualMenuItemType.Header });
        }

        if (canApprove) {
            subitems.push({
                key: 'approve',
                text: 'Approve items',
                iconProps: { iconName: 'CheckMark' },
                onClick: () => setActiveSurface('items.approve')
            });
        }


        if (canReject) {
            subitems.push({
                key: 'reject',
                text: 'Reject items',
                iconProps: { iconName: 'Cancel' },
                onClick: () => setActiveSurface('items.reject')
            });
        }

        if (canApprovePricing || canRejectPricing) {
            subitems.push({ key: 'separator_1', itemType: ContextualMenuItemType.Divider });
            subitems.push({ key: 'header_pricing', text: 'Pricing Approval', itemType: ContextualMenuItemType.Header });
        }

        if (canApprovePricing) {
            subitems.push({
                key: 'approve_pricing',
                text: 'Approve pricing',
                iconProps: { iconName: 'CheckMark' },
                onClick: () => setActiveSurface('items.pricing.approve')
            });
        }

        if (canRejectPricing) {
            subitems.push({
                key: 'reject_pricing',
                text: 'Reject pricing',
                iconProps: { iconName: 'Cancel' },
                onClick: () => setActiveSurface('items.pricing.reject')
            });
        }

        if (subitems.length > 0) {
            items.push({
                key: 'approval',
                text: 'Approval',
                onRender: () => {
                    return <Stack verticalAlign={'center'}>
                        <ActionButton text={'Bulk approve/reject'}
                            menuProps={{ items: subitems }}
                            styles={{ root: { marginRight: 10 } }} />
                    </Stack>
                }
            });
        }

        const canUpdatePrice = loaded && selected.length > 0 && (selected.findIndex((s) => s.pricingStatus === 'approved') <= 0) && PermissionsService.hasPermission(['vessels.requisitions.pricings.update'], user.permissions);
        if (canUpdatePrice) {
            items.push({
                key: 'updatePrice',
                text: 'Update price',
                onRender: () => {
                    return <Stack verticalAlign={'center'}>
                        <ActionButton iconProps={{ iconName: "UpdatePrice" }}
                            text={'Update price'}
                            styles={{ root: { marginRight: 10 } }}
                            onClick={() => setActiveSurface('items.pricing.update')} />
                    </Stack>
                }
            });
        }

        farItems.push({
            key: 'filter',
            text: 'Filter',
            onRender: () => {
                return <Stack tokens={{ childrenGap: 5 }} styles={{ root: { marginLeft: 50 } }} horizontal verticalAlign={'center'}>
                    <Text size={'small'}>Select status: </Text>
                    <Dropdown options={[{ key: "all", text: "All status" }, ...VesselRequisitionItemStatus]}
                        styles={{ root: { width: 150 } }}
                        selectedKey={selectedStatus}
                        onChange={(evt, opt) => {
                            const status = opt?.key as string;
                            setSelectedStatus(status);
                            setSelected([]);
                            setFilteredItems(allItems.filter((i) => status === 'all' || status === i.approvalStatus));
                        }} />
                </Stack>
            }
        })

        return { items, farItems };
    }

    const _onRetrieveItems = async () => {
        setLoaded(false);

        const qs: string[] = ['top=all'];
        const _items = await VesselRequisitionItemService.retrieve(props.requisitionId, qs.join("&"));

        setAllItems(_items.data);
        setSelected([]);
        setFilteredItems(_items.data.filter((i) => selectedStatus === 'all' || selectedStatus === i.approvalStatus));
        setLoaded(true);
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        if (refresh) { _onRetrieveItems(); }
        setActiveSurface(undefined);
    }

    return <Stack>
        <CommandBar {...getCommandBarItems()} styles={{ root: { padding: 0 } }} />
        <ShimmeredDetailsList items={filteredItems}
            columns={columns}
            selection={selection}
            shimmerLines={5}
            enableShimmer={!loaded}
            selectionMode={SelectionMode.multiple} />
        {activeSurface === 'items.approve' ? <ApproveVesselRequisitionItem requisitionId={props.requisitionId} items={selected} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'items.reject' ? <RejectVesselRequisitionItem requisitionId={props.requisitionId} items={selected} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'items.pricing.approve' ? <ApproveVesselRequisitionItemPricing requisitionId={props.requisitionId} items={selected} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'items.pricing.reject' ? <RejectVesselRequisitionItemPricing requisitionId={props.requisitionId} items={selected} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'items.pricing.update' ? <UpdateVesselRequisitionItemPricing items={selected} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
});

export default VesselRequisitionItemsList;
